import React, { useEffect } from 'react';
import { useRouter } from 'next/router';
import MetaTag from '@/components/ui/MetaTag';
import JsonLd from '@/components/ui/JsonLd';
import { BUSINESS } from '@/constants/business';
import { JournalArticle } from './const';
import type { GoldPrices } from './prices';
import type { ExchangeRates, UsdRate } from './forex';
import type { DiamondEstimate } from './diamond-prices';
import { ForexTable } from './forex-table';
import { A, Breadcrumb, Button, CardGrid, Eyebrow, ExploreMore, JournalShell, Section } from './components';
import { Delta, fmtChi } from './gold-card';
import { PriceTabs } from './price-tabs';
import { DiamondCalculator } from './diamond-calculator';
import { DiamondCatalogue } from './diamond-catalogue-view';
import type { DiamondCatalogueData } from './diamond-catalogue-server';
import { PriceGuide } from './price-guide';
import { marketFragmentPath, MARKET_PAGES, MarketPage } from './market-pages';

const SITE = BUSINESS.appUrl;
const formatSourceTime = (value: string) => new Intl.DateTimeFormat('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Ho_Chi_Minh' }).format(new Date(value));
export type JournalPriceProps = { articles?: JournalArticle[]; page?: MarketPage; gold: GoldPrices | null; usd?: UsdRate | null; exchangeRates?: ExchangeRates | null; diamond?: DiamondEstimate | null; catalogue?: DiamondCatalogueData | null };

const GoldTable = ({ gold }: { gold: GoldPrices | null }) => <>
      <div className="tw-mx-auto tw-w-full tw-max-w-[1296px] tw-px-5 md:tw-px-8 tw-scroll-mt-6">
        <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2 tw-border tw-border-solid tw-border-[#DECA95] tw-bg-[#FEF8E0]/60 tw-px-5 tw-py-4 tw-text-[15px]">
          <p className="tw-m-0">Đơn vị: {gold?.unit || 'Nghìn đồng/chỉ'}</p>
          <p className="tw-m-0 tw-text-[#AF9454]">{gold ? `${gold.source} · Cập nhật ${formatSourceTime(gold.updatedAtIso)}` : 'Chưa có dữ liệu'}</p>
        </div>
      </div>

      <Section className="!tw-pt-6 !tw-pb-6">
        <div className="tw-overflow-hidden tw-border tw-border-solid tw-border-[#DECA95] tw-bg-white">
          <table className="tw-w-full tw-table-fixed tw-border-collapse tw-text-left tw-text-[#30220E]">
            <caption className="tw-sr-only">Giá vàng mua vào, bán ra theo thương hiệu, đơn vị nghìn đồng/chỉ</caption>
            <colgroup><col /><col className="tw-w-[88px] md:tw-w-[260px]" /><col className="tw-w-[88px] md:tw-w-[260px]" /></colgroup>
            <thead className="tw-bg-[#FEF8E0] tw-text-[14px] tw-text-[#AF9454]"><tr>
              <th scope="col" className="tw-px-3 md:tw-px-5 tw-py-4 tw-font-normal">Sản phẩm</th>
              <th scope="col" className="tw-px-3 md:tw-px-5 tw-py-4 tw-text-right tw-font-normal">Mua vào</th>
              <th scope="col" className="tw-px-3 md:tw-px-5 tw-py-4 tw-text-right tw-font-normal">Bán ra</th>
            </tr></thead>
            {!gold && <tbody><tr><td colSpan={3} className="tw-px-5 tw-py-8 tw-text-jr-mute">Chưa có dữ liệu giá vàng. Vui lòng quay lại sau ít phút.</td></tr></tbody>}
            {gold?.groups.map((group) => <tbody key={group.brand}>
              <tr className="tw-bg-[#FEFCEF]"><th colSpan={3} scope="rowgroup" className="tw-px-3 md:tw-px-5 tw-py-2 tw-text-[13px] tw-font-normal tw-text-[#AF9454]"><span className="tw-border-0 tw-border-l-2 tw-border-solid tw-border-[#AF9454] tw-pl-2">{group.brand}</span></th></tr>
              {group.rows.map((row) => <tr key={row.key} className="tw-border-0 tw-border-t tw-border-solid tw-border-[#DECA95]/50">
                <th scope="row" className="tw-px-3 md:tw-px-5 tw-py-4 tw-align-top tw-text-[15px] md:tw-text-[17px] tw-font-normal">{row.name}</th>
                <td className="tw-px-3 md:tw-px-5 tw-py-4 tw-align-top tw-text-right tw-tabular-nums tw-text-[18px] md:tw-text-[22px] tw-font-bold">{fmtChi(row.buy)}<Delta value={row.buyDelta} /></td>
                <td className="tw-px-3 md:tw-px-5 tw-py-4 tw-align-top tw-text-right tw-tabular-nums tw-text-[18px] md:tw-text-[22px] tw-font-bold">{fmtChi(row.sell)}<Delta value={row.sellDelta} /></td>
              </tr>)}
            </tbody>)}
          </table>
          {gold && <p className="tw-m-0 tw-flex tw-flex-wrap tw-justify-between tw-gap-x-4 tw-bg-[#FEF8E0] tw-px-4 md:tw-px-5 tw-py-3 tw-text-[13px] tw-text-[#AF9454]"><span>Nguồn {gold.source} · {gold.unit}</span><span>Cập nhật {formatSourceTime(gold.updatedAtIso)}</span></p>}
        </div>
        <p className="tw-mt-5 tw-mb-0 tw-text-[14px] tw-text-jr-mute">Giá giao dịch tại An Thư được xác nhận theo sản phẩm và thời điểm tư vấn.</p>
      </Section>


</>;

const MarketOverview = () => <Section className="!tw-pt-0 !tw-pb-6">
  <div className="tw-grid tw-gap-4 md:tw-grid-cols-3">
    {([
      ['gold', 'Giá vàng trong nước', 'So sánh giá mua vào, bán ra theo thương hiệu, đọc mức chênh lệch và biến động.'],
      ['forex', 'Tỷ giá ngoại tệ', 'Tra cứu USD, EUR, JPY và các ngoại tệ Vietcombank công bố. Lọc nhanh theo tên hoặc mã.'],
      ['diamond', 'Giá kim cương tự nhiên', 'Chọn thông số 4C để xem giá tham khảo quốc tế; nhập số báo cáo để đối chiếu trên GIA.'],
    ] as const).map(([key, title, description], index) => <A key={key} href={MARKET_PAGES[key].path} className="tw-group tw-flex tw-flex-col tw-rounded-lg tw-border tw-border-jr-line tw-bg-white tw-p-6 hover:tw-border-jr-green">
      <span className="tw-text-[12px] tw-tracking-widest tw-text-jr-gold">0{index + 1}</span>
      <h2 className="tw-font-jr tw-mt-4 tw-mb-0 tw-text-[24px] tw-font-normal tw-text-jr-ink">{title}</h2>
      <p className="tw-mt-4 tw-mb-5 tw-flex-1 tw-text-[14px] tw-leading-relaxed tw-text-jr-mute">{description}</p>
      <span className="tw-text-[14px] tw-font-medium tw-text-jr-green group-hover:tw-underline">Mở bảng giá →</span>
    </A>)}
  </div>
</Section>;

export const JournalPriceLayout = ({ page = 'overview', gold, usd = null, exchangeRates = null, diamond = null, catalogue = null }: JournalPriceProps) => {
  const router = useRouter();
  const info = MARKET_PAGES[page];
  const url = `${SITE}${info.path}`;
  const modified = page === 'gold' ? gold?.updatedAtIso : page === 'forex' ? exchangeRates?.updatedAt : page === 'diamond' ? diamond?.updatedAt : undefined;
  // Old entry-point fragments survive the redirect to the default diamond tool.
  useEffect(() => {
    const openLegacyTool = () => {
      const destination = marketFragmentPath(window.location.hash.slice(1));
      if (destination && destination !== info.path) router.replace(destination, undefined, { scroll: false });
    };
    openLegacyTool();
    window.addEventListener('hashchange', openLegacyTool);
    return () => window.removeEventListener('hashchange', openLegacyTool);
  }, [info.path, router]);
  const crumbs = [
    { label: 'Trang chủ', href: '/' },
    { label: 'Bảng giá', ...(page === 'overview' ? {} : { href: MARKET_PAGES.overview.path }) },
    ...(page === 'overview' ? [] : [{ label: info.label }]),
  ];
  return <JournalShell activeKey="bang-gia">
    <MetaTag title={info.title} description={info.description} handle={info.path} image={`${SITE}/logo-bg.png`} />
    <JsonLd schema={[
      { '@context': 'https://schema.org', '@type': 'BreadcrumbList', itemListElement: crumbs.map((crumb, index) => ({ '@type': 'ListItem', position: index + 1, name: crumb.label, item: `${SITE}${'href' in crumb && crumb.href ? crumb.href : info.path}` })) },
      { '@context': 'https://schema.org', '@type': page === 'overview' ? 'CollectionPage' : 'WebPage', '@id': `${url}#page`, url, name: info.title, description: info.description, inLanguage: 'vi', publisher: { '@id': BUSINESS.id }, ...(modified ? { dateModified: modified } : {}) },
    ]} />
    <Breadcrumb items={crumbs} />
    <section>
      <div className="tw-mx-auto tw-w-full tw-max-w-[1296px] tw-px-5 md:tw-px-8 tw-pt-6 md:tw-pt-8 tw-pb-6 md:tw-pb-8">
        <Eyebrow>AN THƯ · BẢNG GIÁ</Eyebrow>
        <h1 className="tw-font-jr tw-mt-3 tw-mb-0 tw-text-[28px] md:tw-text-[44px] lg:tw-text-[48px] tw-font-normal tw-leading-[1.15]">{info.heading}</h1>
        <p className="tw-mt-3 tw-mb-0 tw-text-[14px] md:tw-text-[17px] tw-text-jr-mute">{info.intro}</p>
      </div>
    </section>
    <PriceTabs active={page} />
    {page === 'overview' && <MarketOverview />}
    {page === 'gold' && <GoldTable gold={gold} />}
    {page === 'forex' && <ForexTable data={exchangeRates} showHeading={false} />}
    {page === 'diamond' && <DiamondCalculator usd={usd} initialEstimate={diamond} showHeading={false} />}
    {page === 'diamond' && catalogue && <DiamondCatalogue data={catalogue} usd={usd} />}
    <PriceGuide page={page} />
      <Section className="!tw-py-8">
        <div className="tw-grid tw-gap-6 md:tw-grid-cols-[1fr_auto] md:tw-items-center tw-bg-jr-band tw-p-6 md:tw-p-10">
          <div>
            <Eyebrow>CÙNG AN THƯ LỰA CHỌN</Eyebrow>
            <h2 className="tw-font-jr tw-mt-3 tw-mb-0 tw-text-[26px] md:tw-text-[34px] tw-font-normal tw-leading-[1.25]">
              Hiểu giá trị. Chọn điều phù hợp.
            </h2>
            <p className="tw-mt-3 tw-mb-0 tw-text-[15px] tw-text-jr-mute">Trang sức kim cương tự nhiên, chế tác từ vàng 14K và 18K.</p>
          </div>
          <div className="tw-flex tw-flex-col tw-gap-3 md:tw-min-w-[280px]">
            <Button href="/danh-muc/trang-suc-kim-cuong-tu-nhien/">Khám phá trang sức</Button>
            <Button href="/accounts/appointment/new/" tone="light">
              Tư vấn giá kim cương
            </Button>
          </div>
        </div>
      </Section>

      <Section className="!tw-pt-4">
        <h2 className="tw-font-jr tw-m-0 tw-mb-8 tw-text-[28px] md:tw-text-[40px] tw-font-normal">Kiến thức về vàng & bảo quản</h2>
        <CardGrid
          cols={3}
          items={[
            { title: '14K, 18K và 24K', desc: 'Tìm hiểu hàm lượng vàng.', href: `/tin-tuc/?q=${encodeURIComponent('vàng')}`, label: 'Tìm hiểu' },
            { title: 'Vàng trắng & bạch kim', desc: 'Hiểu khác biệt về chất liệu.', href: `/tin-tuc/?q=${encodeURIComponent('vàng trắng')}`, label: 'Tìm hiểu' },
            { title: 'Chăm sóc trang sức', desc: 'Giữ vẻ đẹp qua từng lần đeo.', href: '/tin-tuc/vang-bao-quan/', label: 'Tìm hiểu' },
          ]}
        />
      </Section>
      <ExploreMore exclude="bang-gia" />
  </JournalShell>;
};
